import React, { useCallback } from 'react';
import { SuggestionItem } from './types';

interface SuggestionDropdownProps {
  suggestions: SuggestionItem[];
  onSelect: (item: SuggestionItem) => void;
}

/**
 * Accessible dropdown that renders filtered suggestion items.
 * Uses ARIA role="listbox" on the container and role="option" on each item.
 * Supports Enter key selection.
 * Only renders when suggestions.length > 0.
 */
export function SuggestionDropdown({ suggestions, onSelect }: SuggestionDropdownProps) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, item: SuggestionItem) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSelect(item);
      }
    },
    [onSelect],
  );

  if (suggestions.length === 0) return null;

  return (
    <div
      id="rv-suggestions-listbox"
      className="rv-suggestions-dropdown"
      role="listbox"
      aria-label="Suggestions"
    >
      {suggestions.map((item) => (
        <div
          key={item.id}
          className="rv-suggestion-item"
          role="option"
          aria-selected={false}
          tabIndex={0}
          onClick={() => onSelect(item)}
          onKeyDown={(e) => handleKeyDown(e, item)}
        >
          <span className="rv-suggestion-category">{item.subcategory || item.category}</span>
          <span className="rv-suggestion-label">{item.label}</span>
          <span className="rv-suggestion-desc">{item.description}</span>
        </div>
      ))}
    </div>
  );
}
